import { useMemo } from "react";
import {
	type ChecklistItem,
	listChecklistItems,
	createChecklistItem,
	updateChecklistItem,
	deleteChecklistItem,
	applyChecklistTemplate,
} from "@/services/task-checklist.service";
import { useApiSWR } from "@/hooks/useApiSWR";

export function useChecklist(projectId: string, taskId: string) {
	const { data, error, isLoading, mutate } = useApiSWR<ChecklistItem[]>(
		["tasks", taskId, "checklist-items"],
		() => listChecklistItems(projectId, taskId),
	);

	const items = useMemo(() => data ?? [], [data]);

	const completed = items.filter((i) => i.is_completed).length;
	const total = items.length;
	const progress = total === 0 ? 0 : Math.round((completed / total) * 100);

	async function addItem(title: string) {
		const trimmed = title.trim();
		if (!trimmed) return;
		await createChecklistItem(projectId, taskId, trimmed);
		await mutate();
	}

	async function toggleItem(item: ChecklistItem) {
		const next = !item.is_completed;
		await mutate(
			(curr) =>
				(curr ?? []).map((i) =>
					i.id === item.id ? { ...i, is_completed: next } : i,
				),
			{ revalidate: false },
		);
		try {
			await updateChecklistItem(projectId, taskId, item.id, {
				is_completed: next,
			});
		} finally {
			await mutate();
		}
	}

	async function renameItem(item: ChecklistItem, title: string) {
		await updateChecklistItem(projectId, taskId, item.id, { title });
		await mutate();
	}

	async function removeItem(item: ChecklistItem) {
		await mutate(
			(curr) => (curr ?? []).filter((i) => i.id !== item.id),
			{ revalidate: false },
		);
		try {
			await deleteChecklistItem(projectId, taskId, item.id);
		} finally {
			await mutate();
		}
	}

	async function applyTemplate(templateId: string) {
		await applyChecklistTemplate(projectId, taskId, templateId);
		await mutate();
	}

	return {
		items,
		completed,
		total,
		progress,
		isLoading,
		error,
		addItem,
		toggleItem,
		renameItem,
		removeItem,
		applyTemplate,
		refresh: mutate,
	};
}
